/* MUBA: text width, preview B (A6). In centred text sections the paragraphs are left-aligned inside a column centred on
   the page (theme.css). One reading column per page: this script measures the longest line of all such paragraphs on
   the page and gives every one of them that same width (--muba-fit, see theme.css), so all text sections of a page
   share the same left edge and the widest text is exactly centred under its heading.

   - Only left-aligned text capped by the text width (--muba-measure) inside a centred section is fitted: centred
     text, full-width text (phones, narrow containers) and left or right sections stay as they are.
   - Columns with a different centre (e.g. text beside an image) get their own width. The column centred on the page
     is also set as --muba-column on <html>, used by the FAQ list so it lines up with the text around it.
   - Fitted paragraphs wrap greedily (data-muba-fit, theme.css). With `text-wrap: pretty` every narrower width re-breaks
     the lines shorter again (whole paragraphs in Safari), so the text would end up left of centre. With greedy
     wrapping the lines at the fitted width are exactly the measured ones: one pass, no loop. */
(() => {
  const BLOCKS = ':scope > :is(p, ul, ol, blockquote, div:not(:has(img, video, iframe, table, .button)))';
  try {
    document.createElement('div').querySelector(BLOCKS);
  } catch (error) {
    return; /* no :has() support: theme.css doesn't narrow the text there either */
  }

  const fittedWidth = new WeakMap(); /* container -> its width after the last fit */
  const containersIn = (root) => [...root.querySelectorAll('.prose--measure')];

  /* Right edge of the longest line, measured word by word (a range over a whole text node can include the space
     where a line wraps) */
  const range = document.createRange();
  const lineEnd = (block) => {
    let right = -Infinity;
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      for (const word of node.data.matchAll(/\S+/g)) {
        range.setStart(node, word.index);
        range.setEnd(node, word.index + word[0].length);
        for (const rect of range.getClientRects()) if (rect.width > 0) right = Math.max(right, rect.right);
      }
    }
    return right;
  };

  const fit = () => {
    const containers = containersIn(document);
    const blocks = containers.flatMap((container) => [...container.querySelectorAll(BLOCKS)].map((block) => ({ container, block })));

    /* 1. Measure with the fit cleared, so a fitted column never shrinks itself further */
    blocks.forEach(({ block }) => block.style.removeProperty('--muba-fit'));

    const candidates = [];
    for (const { container, block } of blocks) {
      const style = getComputedStyle(block);
      const box = block.getBoundingClientRect();
      const eligible =
        getComputedStyle(container).textAlign === 'center' &&
        /^(start|left)$/.test(style.textAlign) &&
        style.direction === 'ltr' &&
        box.width > 0 &&
        box.width >= parseFloat(style.maxWidth) - 1;
      if (eligible) {
        block.setAttribute('data-muba-fit', '');
        candidates.push({ block, style, centre: box.left + box.width / 2 });
      } else {
        block.removeAttribute('data-muba-fit');
      }
    }

    /* 2. The longest line per column centre (normally one column per page) */
    const columns = [];
    for (const candidate of candidates) {
      const { block, style, centre } = candidate;
      const right = lineEnd(block);
      if (right === -Infinity) continue;
      const box = block.getBoundingClientRect();
      const end = parseFloat(style.paddingRight) + parseFloat(style.borderRightWidth);
      const start = parseFloat(style.paddingLeft) + parseFloat(style.borderLeftWidth);
      const width = right - box.left + (style.boxSizing === 'border-box' ? end : -start);
      let column = columns.find((other) => Math.abs(other.centre - centre) <= 1);
      if (!column) columns.push((column = { centre, width: 0, blocks: [] }));
      column.width = Math.max(column.width, width);
      column.blocks.push(block);
    }

    /* 3. One width per column, so all its paragraphs share the same left edge */
    for (const column of columns) column.blocks.forEach((block) => block.style.setProperty('--muba-fit', `${Math.ceil(column.width)}px`));

    /* 4. The column centred on the page is also offered to other centred sections (FAQ, theme.css) as --muba-column */
    const pageCentre = document.documentElement.clientWidth / 2;
    const pageColumn = columns.find((column) => Math.abs(column.centre - pageCentre) <= 1);
    if (pageColumn) document.documentElement.style.setProperty('--muba-column', `${Math.ceil(pageColumn.width)}px`);
    else document.documentElement.style.removeProperty('--muba-column');
    for (const container of containers) fittedWidth.set(container, container.getBoundingClientRect().width);
  };

  let frame = 0;
  const refit = () => {
    /* Next frame, not inside a ResizeObserver callback: resizing there would trigger a ResizeObserver loop warning */
    if (!frame) {
      frame = requestAnimationFrame(() => {
        frame = 0;
        fit();
      });
    }
  };

  const observer = new ResizeObserver((entries) => {
    const changed = entries.some(({ target }) => Math.abs((fittedWidth.get(target) ?? -1) - target.getBoundingClientRect().width) > 0.5);
    if (changed) refit();
  });

  const observe = (root) => containersIn(root).forEach((container) => observer.observe(container));

  observe(document);
  fit();
  document.fonts?.ready.then(refit);
  document.fonts?.addEventListener('loadingdone', refit);
  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
    refit();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    containersIn(event.target).forEach((container) => observer.unobserve(container));
    refit(); /* runs after the editor removed the section */
  });
})();
